import { useEffect, useState } from 'react';
import Select from 'react-select';

// Grouped language options for better UX
const langOptions = [
    {
        label: 'Primary Languages',
        options: [
            { value: 'guj', label: 'Gujarati', isFixed: true },
            { value: 'eng', label: 'English', isFixed: true },
            { value: 'hin', label: 'Hindi', isFixed: false },
        ],
    },
    {
        label: 'Secondary Languages',
        options: [
            { value: 'spa', label: 'Spanish', isFixed: false },
            { value: 'fre', label: 'French', isFixed: false },
            { value: 'ger', label: 'German', isFixed: false },
            { value: 'rus', label: 'Russian', isFixed: false },
            { value: 'por', label: 'Portuguese', isFixed: false },
            { value: 'tel', label: 'Telugu', isFixed: false },
            { value: 'kan', label: 'Kannada', isFixed: false },
            { value: 'chi', label: 'Mandarin', isFixed: false },
            { value: 'kor', label: 'Korean', isFixed: false },
        ],
    },
    {
        label: 'European Languages',
        options: [
            { value: 'ita', label: 'Italian', isFixed: false },
            { value: 'dut', label: 'Dutch', isFixed: false },
            { value: 'pol', label: 'Polish', isFixed: false },
            { value: 'tur', label: 'Turkish', isFixed: false },
            { value: 'gre', label: 'Greek', isFixed: false },
            { value: 'swe', label: 'Swedish', isFixed: false },
            { value: 'nor', label: 'Norwegian', isFixed: false },
            { value: 'dan', label: 'Danish', isFixed: false },
            { value: 'fin', label: 'Finnish', isFixed: false },
            { value: 'hun', label: 'Hungarian', isFixed: false },
            { value: 'cze', label: 'Czech', isFixed: false },
            { value: 'rum', label: 'Romanian', isFixed: false },
            { value: 'slv', label: 'Slovenian', isFixed: false },
            { value: 'cat', label: 'Catalan', isFixed: false },
        ],
    },
    {
        label: 'Asian Languages',
        options: [
            { value: 'jpn', label: 'Japanese', isFixed: false },
            { value: 'tha', label: 'Thai', isFixed: false },
            { value: 'vie', label: 'Vietnamese', isFixed: false },
            { value: 'ind', label: 'Indonesian', isFixed: false },
            { value: 'may', label: 'Malay', isFixed: false },
            { value: 'bur', label: 'Burmese', isFixed: false },
            { value: 'khm', label: 'Khmer', isFixed: false },
            { value: 'lao', label: 'Lao', isFixed: false },
            { value: 'mon', label: 'Mongolian', isFixed: false },
            { value: 'tib', label: 'Tibetan', isFixed: false },
        ],
    },
    {
        label: 'Indian Languages',
        options: [
            { value: 'tam', label: 'Tamil', isFixed: false },
            { value: 'ben', label: 'Bengali', isFixed: false },
            { value: 'mar', label: 'Marathi', isFixed: false },
            { value: 'mal', label: 'Malayalam', isFixed: false },
            { value: 'urd', label: 'Urdu', isFixed: false },
            { value: 'pun', label: 'Punjabi', isFixed: false },
            { value: 'sin', label: 'Sinhala', isFixed: false },
            { value: 'nep', label: 'Nepali', isFixed: false },
        ],
    },
    {
        label: 'Middle Eastern & African Languages',
        options: [
            { value: 'ara', label: 'Arabic', isFixed: false },
            { value: 'per', label: 'Persian', isFixed: false },
            { value: 'heb', label: 'Hebrew', isFixed: false },
            { value: 'swa', label: 'Swahili', isFixed: false },
            { value: 'hau', label: 'Hausa', isFixed: false },
            { value: 'yor', label: 'Yoruba', isFixed: false },
            { value: 'ibo', label: 'Igbo', isFixed: false },
            { value: 'amh', label: 'Amharic', isFixed: false },
            { value: 'som', label: 'Somali', isFixed: false },
            { value: 'xho', label: 'Xhosa', isFixed: false },
            { value: 'zul', label: 'Zulu', isFixed: false },
            { value: 'arm', label: 'Armenian', isFixed: false },
            { value: 'geo', label: 'Georgian', isFixed: false },
        ],
    },
    {
        label: 'Other Languages',
        options: [
            { value: 'tag', label: 'Tagalog', isFixed: false },
            { value: 'que', label: 'Quechua', isFixed: false },
            { value: 'uzb', label: 'Uzbek', isFixed: false },
            { value: 'kaz', label: 'Kazakh', isFixed: false },
        ],
    },
];

// Flatten all options for backward compatibility
const flatLangOptions = langOptions.reduce((acc, group) => {
    return acc.concat(group.options);
}, []);

const GUJARATI_LANGUAGE_INDEX = 0;
const ENGLISH_LANGUAGE_INDEX = 1;
const HINDI_LANGUAGE_INDEX = 2;
const ADMIN = 'Admin';
const VIEW_ONLY = 'View-Only';

const styles = {
    multiValue: (base, state) => {
        return state.isFixed ? { ...base, backgroundColor: 'white' } : base;
    },
    multiValueLabel: (base, state) => {
        return state.isFixed ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 } : base;
    },
    multiValueRemove: (base, state) => {
        return state.isFixed ? { ...base, display: 'none' } : base;
    },
};

function getLanguageNames(languages) {
    return languages.map((lang) => lang.label);
}

export function getLanguages(readFromVideoData, filterLanguagesBeingProcessed) {
    if (readFromVideoData) {
        // When displaying in the edit view
        const videoPropsLng = JSON.parse(localStorage.getItem('videoProps'));
        let languageNames = [];
        if (filterLanguagesBeingProcessed) {
            languageNames = (videoPropsLng?.videoLanguages || [])
                .filter((item) => item.status !== 'PROCESSING')
                .map((item) => item.languageName);
        } else {
            languageNames = (videoPropsLng?.videoLanguages || []).map((item) => item.languageName);
        }
        return languageNames;
    } else {
        // When displaying the create video dropdown
        return langOptions.filter((item) => item.value !== 'Gujarati');
    }
}

export function getLanguagesForEditView() {
    let languagesSupported = getLanguages(true, true);

    // Remove Gujarati from this list, We do not want to edit Gujarati subtitles, if that changes in future, remove this line
    //languagesSupported = languagesSupported.filter(lang => lang !== 'Gujarati');

    // filter these out and only keep the language that was selected in the main page and English
    return languagesSupported;
}

export function getLanguagesForMainPage() {
    return getLanguageNames(getLanguages(false, false));
}

export function getLanguagesForVideoList() {}

export function getLanguagesForCreateVideo() {
    return getLanguages(false, false);
}

export function SelectLanguageComponent(props) {
    // Extract additional props
    const {
        darkMode = false,
        themeVariant = 'vibrant',
        disabled,
        languageOptions,
        initialLanguages,
        showQuickPresets = false,
        customOptions = null,
        customStyles = null,
        maxMenuHeight = 800,
        placeholder = 'Search and select languages...',
        onChange: onChangeCallback,
        onLanguagesChange,
    } = props;

    // Use custom options if provided, otherwise use default grouped options
    const optionsToUse = customOptions || langOptions;

    const getInitialLanguages = () => {
        // initialLanguages sets which are pre-selected without restricting available options
        if (initialLanguages && initialLanguages.length > 0) {
            return flatLangOptions.filter((v) => initialLanguages.includes(v.label));
        }
        if (!props.languageOptions || props.languageOptions.length === 0) {
            // If using custom options, don't set any default languages
            if (customOptions) {
                return [];
            }
            // Only set default languages for non-custom usage
            return [
                flatLangOptions.find((lang) => lang.value === 'guj'),
                flatLangOptions.find((lang) => lang.value === 'eng'),
                flatLangOptions.find((lang) => lang.value === 'hin'),
            ].filter(Boolean); // Remove any undefined values
        } else {
            // Handle both old format (language names) and new format (language objects)
            if (customOptions) {
                // For custom options, we need to find from all groups
                const allCustomOptions = customOptions.reduce((acc, group) => {
                    return acc.concat(group.options || []);
                }, []);
                return allCustomOptions.filter((v) => props.languageOptions.includes(v.originalLabel || v.label));
            } else {
                return flatLangOptions.filter((v) => props.languageOptions.includes(v.label));
            }
        }
    };

    const [languages, setLanguages] = useState(getInitialLanguages);

    // Update the useEffect to handle prop changes properly
    // Note: intentionally excludes initialLanguages — that prop is for initial state only,
    // not for reactive updates. Reacting to it would reset user selections on every render.
    useEffect(() => {
        if (props.initialLanguages) return; // initialLanguages-based selection is set once via useState only
        const newLanguages = getInitialLanguages();
        setLanguages(newLanguages);
    }, [props.languageOptions, customOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem('createVideoLanguages', JSON.stringify(getLanguageNames(languages)));
        // Unlike onChange, this also reports the starting languages and Select All / Clear
        if (onLanguagesChange) {
            onLanguagesChange(getLanguageNames(languages));
        }
    }, [languages]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChange = (newValue, actionMeta) => {
        switch (actionMeta.action) {
            case 'remove-value':
            case 'pop-value':
                if (actionMeta.removedValue.isFixed) {
                    return;
                }
                break;
            case 'clear':
                const fixedOptions = customOptions
                    ? customOptions.reduce((acc, group) => acc.concat(group.options || []), []).filter((v) => v.isFixed)
                    : flatLangOptions.filter((v) => v.isFixed);
                newValue = fixedOptions;
                break;
            case 'select-option':
                break;
            default:
                break;
        }

        console.log(newValue);
        const languageNames = getLanguageNames(newValue || []);
        localStorage.setItem('createVideoLanguages', JSON.stringify(languageNames));
        setLanguages(newValue || []);

        // Call the custom onChange if provided
        if (onChangeCallback) {
            onChangeCallback(newValue || [], actionMeta);
        }
    };

    const selectAllInGroup = (groupOptions) => {
        const currentValues = new Set(languages.map((lang) => lang.value));
        const newLanguages = [...languages];

        groupOptions.forEach((option) => {
            if (!currentValues.has(option.value) && !option.isDisabled) {
                newLanguages.push(option);
            }
        });

        setLanguages(newLanguages);
        if (onChangeCallback) {
            onChangeCallback(newLanguages, { action: 'select-group' });
        }
    };

    const clearAllInGroup = (groupOptions) => {
        const groupValues = new Set(groupOptions.map((opt) => opt.value));
        const newLanguages = languages.filter((lang) => !groupValues.has(lang.value) || lang.isFixed);
        setLanguages(newLanguages);
        if (onChangeCallback) {
            onChangeCallback(newLanguages, { action: 'clear-group' });
        }
    };

    const disableComponent = props.disabled;

    const getCustomStyles = (isDarkMode) => {
        const defaultStyles = {
            multiValue: (base, state) => {
                return state.data.isFixed
                    ? {
                          ...base,
                          backgroundColor: isDarkMode ? '#1976d2' : '#1976d2',
                          color: 'white',
                      }
                    : {
                          ...base,
                          backgroundColor: isDarkMode ? '#444' : '#e8e8e8',
                      };
            },
            multiValueLabel: (base, state) => {
                return state.data.isFixed
                    ? {
                          ...base,
                          fontWeight: 'bold',
                          color: 'white',
                          paddingRight: 6,
                      }
                    : {
                          ...base,
                          color: isDarkMode ? '#e0e0e0' : 'black',
                      };
            },
            multiValueRemove: (base, state) => {
                return state.data.isFixed
                    ? { ...base, display: 'none' }
                    : {
                          ...base,
                          color: isDarkMode ? '#ccc' : '#666',
                          ':hover': {
                              backgroundColor: isDarkMode ? '#666' : '#ccc',
                              color: isDarkMode ? 'white' : 'black',
                          },
                      };
            },
            control: (base, state) => ({
                ...base,
                backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                borderColor: state.isFocused ? (isDarkMode ? '#1976d2' : '#1976d2') : isDarkMode ? '#444' : '#ccc',
                boxShadow: state.isFocused ? `0 0 0 1px ${isDarkMode ? '#1976d2' : '#1976d2'}` : 'none',
                minHeight: 38,
                '&:hover': {
                    borderColor: isDarkMode ? '#666' : '#999',
                },
            }),
            menu: (base) => ({
                ...base,
                backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                zIndex: 9999,
                border: `1px solid ${isDarkMode ? '#444' : '#ccc'}`,
                boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: `${maxMenuHeight}px`,
            }),
            menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
            }),
            menuList: (base) => ({
                ...base,
                maxHeight: `${maxMenuHeight}px`,
                overflowY: 'auto',
                padding: '0',
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused
                    ? isDarkMode
                        ? '#333'
                        : '#f0f0f0'
                    : state.isSelected
                    ? isDarkMode
                        ? '#1976d2'
                        : '#1976d2'
                    : isDarkMode
                    ? '#2d2d2d'
                    : '#fff',
                color: state.isSelected ? '#fff' : isDarkMode ? '#e0e0e0' : '#000',
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                opacity: state.isDisabled ? 0.6 : 1,
                padding: '10px 12px',
                fontSize: '14px',
                borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                '&:hover': {
                    backgroundColor: state.isSelected
                        ? isDarkMode
                            ? '#1976d2'
                            : '#1976d2'
                        : isDarkMode
                        ? '#333'
                        : '#f0f0f0',
                },
            }),
            groupHeading: (provided, state) => ({
                ...provided,
                color: isDarkMode ? '#90caf9' : '#1976d2',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                padding: '8px 12px 4px 12px',
                margin: 0,
                backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5',
                borderBottom: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }),
            singleValue: (base) => ({
                ...base,
                color: isDarkMode ? '#e0e0e0' : 'black',
            }),
            input: (base) => ({
                ...base,
                color: isDarkMode ? '#e0e0e0' : 'black',
            }),
            placeholder: (base) => ({
                ...base,
                color: isDarkMode ? '#999' : '#666',
                fontSize: '14px',
            }),
            indicatorSeparator: (base) => ({
                ...base,
                backgroundColor: isDarkMode ? '#444' : '#ccc',
            }),
            dropdownIndicator: (base) => ({
                ...base,
                color: isDarkMode ? '#999' : '#666',
                '&:hover': {
                    color: isDarkMode ? '#ccc' : '#333',
                },
            }),
            clearIndicator: (base) => ({
                ...base,
                color: isDarkMode ? '#999' : '#666',
                '&:hover': {
                    color: isDarkMode ? '#ccc' : '#333',
                },
            }),
        };

        // Merge custom styles if provided - FIXED VERSION
        if (customStyles) {
            const mergedStyles = {};
            const allKeys = new Set([...Object.keys(defaultStyles), ...Object.keys(customStyles)]);

            allKeys.forEach((key) => {
                mergedStyles[key] = (base, state) => {
                    const defaultStyle = defaultStyles[key] ? defaultStyles[key](base, state) : base;
                    const customStyle = customStyles[key] ? customStyles[key](defaultStyle, state) : defaultStyle;
                    return customStyle;
                };
            });

            return mergedStyles;
        }

        return defaultStyles;
    };

    const formatGroupLabel = (data) => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <span>{data.label}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
                <button
                    style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        border: 'none',
                        borderRadius: '3px',
                        backgroundColor: darkMode ? '#1976d2' : '#1976d2',
                        color: 'white',
                        cursor: 'pointer',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        selectAllInGroup(data.options);
                    }}
                >
                    Select All
                </button>
                <button
                    style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        border: 'none',
                        borderRadius: '3px',
                        backgroundColor: darkMode ? '#666' : '#999',
                        color: 'white',
                        cursor: 'pointer',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        clearAllInGroup(data.options);
                    }}
                >
                    Clear
                </button>
            </div>
        </div>
    );

    return (
        <div style={{ position: 'relative' }}>
            <Select
                id="selectLanguage"
                value={languages}
                isMulti
                styles={getCustomStyles(darkMode)}
                isClearable={languages.some((v) => !v.isFixed)}
                name="colors"
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={onChange}
                options={optionsToUse}
                isDisabled={disableComponent}
                placeholder={placeholder}
                isSearchable={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                formatGroupLabel={formatGroupLabel}
                maxMenuHeight={maxMenuHeight}
                filterOption={(option, inputValue) => {
                    const searchLabel = option.originalLabel || option.label;
                    return (
                        searchLabel.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.value.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }}
                components={{
                    Option: ({ children, ...props }) => (
                        <div
                            {...props.innerProps}
                            style={{
                                ...props.getStyles('option', props),
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span>{children}</span>
                            {props.isSelected && <span>✓</span>}
                        </div>
                    ),
                }}
            />
            <div
                style={{
                    fontSize: '11px',
                    color: darkMode ? '#999' : '#666',
                    marginTop: '4px',
                }}
            >
                {languages.length} language{languages.length !== 1 ? 's' : ''} selected
            </div>
        </div>
    );
}

// Export both grouped and flat options for other components
export { flatLangOptions, langOptions as groupedLangOptions };
